/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: 231 lines
 * - Adding: ~3 lines
 * - Projected: 234 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts, auth components
 */

import { FunctionComponent, useState } from "react";
import { useFormik } from "formik";
import { NavigateFunction } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { checkUser, resendVerificationEmail } from "../../services/usersService";
import { googleAuth } from "../../services/googleServices";
import { LoginCredentials } from "../../interfaces/User";
import { loginValidationSchema } from "./validationSchemas";
import PasswordInput from "./PasswordInput";
import GoogleAuthButton from "./GoogleAuthButton";
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    UI_STANDARDIZATION
 * Date:         2025-01-XX
 * Modified By:  Cursor
 *
 * Description:
 * Replaced direct toast.* calls with centralized notify helper for consistent messaging.
 *
 * Related Screens / Modules:
 * - client/src/components/auth/LoginForm.tsx
 * - client/src/utils/notifications.ts
 */

import { notify } from "../../utils/notifications";

interface LoginFormProps {
  navigate: NavigateFunction;
  onAuthSuccess?: () => void;
  onClose: () => void;
}

const LoginForm: FunctionComponent<LoginFormProps> = ({ navigate, onAuthSuccess, onClose }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [loginError, setLoginError] = useState<string>("");
  // [2026-06-23] KEEP_UNVERIFIED_LEADS: resend verification link from login screen
  const [showResend, setShowResend] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Custom handlers to clear error when user types
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (loginError) setLoginError("");
    if (showResend) setShowResend(false);
    formik.handleChange(e);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (loginError) setLoginError("");
    formik.handleChange(e);
  };

  // [2026-06-23] KEEP_UNVERIFIED_LEADS: resend the verification link to the entered email
  const handleResendVerification = async () => {
    if (isResending || resendCooldown > 0) return;
    setIsResending(true);
    try {
      await resendVerificationEmail(formik.values.email);
      notify.success("We've sent you a new confirmation link. Please check your inbox (and spam folder)");
      setShowResend(false);
    } catch (err: any) {
      // 429 = cooldown still active; surface the remaining seconds
      if (err?.response?.status === 429) {
        const wait = Number(err.response.data?.cooldownSeconds) || 60;
        setResendCooldown(wait);
        const timer = setInterval(() => {
          setResendCooldown((s) => {
            if (s <= 1) { clearInterval(timer); return 0; }
            return s - 1;
          });
        }, 1000);
        notify.error(null, `Please wait ${wait}s before requesting another link`);
      } else {
        notify.error(err, "We couldn't resend the confirmation email. Please try again");
      }
    } finally {
      setIsResending(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setIsGoogleLoading(true);
      try {
        // Exchange Google ID token for our backend JWT (cookie is set by server)
        await googleAuth(tokenResponse.access_token);
        // [2026-02-11] Cookie auth: Token is now set via HTTP-only cookie by server
        navigate("/");
        onAuthSuccess?.();
        onClose();
      } catch (error: any) {
        // Structured console logging
        if (process.env.NODE_ENV === "development") {
          console.group("🔍 Google login error");
          console.error("Error object:", error);
          if (error.status) console.log("HTTP status:", error.status);
          if (error.code) console.log("Backend code:", error.code);
          if (error.raw) console.log("Backend payload:", error.raw);
          console.groupEnd();
        } else {
          console.error("Google login error:", error?.message || error);
        }

        // User-facing message - use server message if available
        let message =
          typeof error?.message === "string" && error.message.trim().length > 0
            ? error.message
            : "We couldn't sign you in with Google. Please try again";

        // [2025-01-13] status-enforcement: handle suspended account error
        if (error?.code === "ACCOUNT_SUSPENDED" || message.includes("suspended")) {
          message = "Your account has been suspended. Please contact support for assistance.";
        }
        // Optional: Improve "User does not exist" message
        else if (message.includes("User does not exist")) {
          message = "No account found for this Google email. Please sign up first.";
        }

        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.error(null, message);
      } finally {
        setIsGoogleLoading(false);
      }
    },
    onError: (error) => {
      console.error("Google login error (popup failed):", error);
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(null, "We couldn't sign you in with Google. Please try again");
      setIsGoogleLoading(false);
    },
  });

  const formik = useFormik<LoginCredentials>({
    initialValues: { email: "", password: "" },
    validationSchema: loginValidationSchema,
    onSubmit: (values) => {
      setLoginError(""); // Clear any previous errors
      checkUser(values)
        .then((res) => {
          if (res.data) {
            // [2026-02-11] Cookie auth: Token is now set via HTTP-only cookie by server
            navigate("/");
            onAuthSuccess?.();
            onClose();
          } else {
            setLoginError("No such user");
          }
        })
        .catch((err) => {
          console.log("\n=== SERVER RESPONSE DEBUG ===");
          console.log("HTTP Status:", err.response?.status);
          console.log("Response Data:", err.response?.data);
          console.log("Response Headers:", err.response?.headers);
          console.log("Request URL:", err.config?.url);
          console.log("Request Method:", err.config?.method);
          console.log("Request Headers:", err.config?.headers);
          console.log("Request Data:", err.config?.data);
          console.log("Error message:", err.message);
          console.log("=============================\n");

          // Display server error message or fallback message
          let errorMessage = "Login failed. Please try again.";

          if (err.response?.data) {
            // Handle email not verified error specifically
            if (
              err.response.data.code === "EMAIL_NOT_VERIFIED" ||
              err.response.data.message?.includes("verify your email")
            ) {
              errorMessage =
                "Please verify your email before logging in. Check your inbox — and your spam/junk folder — for the verification email.";
              setLoginError(errorMessage);
              // [2026-06-23] KEEP_UNVERIFIED_LEADS: offer a resend button for unverified users
              setShowResend(true);
              return;
            }

            // [2025-01-13] status-enforcement: handle suspended account error
            if (
              err.response.data.code === "ACCOUNT_SUSPENDED" ||
              err.response.data.message?.includes("suspended")
            ) {
              errorMessage =
                "Your account has been suspended. Please contact support for assistance.";
              setLoginError(errorMessage);
              return;
            }

            // Handle different error response formats
            if (typeof err.response.data === "string") {
              errorMessage = err.response.data;
            } else if (err.response.data.error) {
              // Handle structured error response { ok: false, error: "..." }
              errorMessage = err.response.data.error;
              // Log additional diagnostic info if present
              if (err.response.data.got) {
                console.log("Server received:", err.response.data.got);
              }
            } else if (err.response.data.errors && Array.isArray(err.response.data.errors)) {
              // Handle validation errors array
              errorMessage = err.response.data.errors.map((e: any) => e.message).join(", ");
            } else if (err.response.data.message) {
              errorMessage = err.response.data.message;
            }
          } else if (err.message) {
            errorMessage = err.message;
          }

          setLoginError(errorMessage);
        });
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} className="d-flex flex-column align-items-center">
      {/* Email */}
      <div className="mb-4 auth-field-container">
        <input
          type="email"
          className="auth-text-18"
          placeholder="Email"
          name="email"
          value={formik.values.email}
          onChange={handleEmailChange}
          onBlur={formik.handleBlur}
        />
        {formik.touched.email && formik.errors.email && (
          <div className="text-danger mt-1">{formik.errors.email}</div>
        )}
      </div>

      {/* Password */}
      <PasswordInput
        name="password"
        placeholder="Password"
        value={formik.values.password}
        onChange={handlePasswordChange}
        onBlur={formik.handleBlur}
        showPassword={showPassword}
        onTogglePassword={() => setShowPassword(!showPassword)}
        error={formik.errors.password}
        touched={formik.touched.password}
      />

      {/* Login Error Message */}
      {loginError && <div className="text-danger mt-2 mb-3">{loginError}</div>}

      {/* [2026-06-23] KEEP_UNVERIFIED_LEADS: resend verification link for unverified users */}
      {showResend && (
        <div className="auth-field-container mb-3">
          <p className="resend-verify-prompt">{"Didn't get it, or the link expired?"}</p>
          <button
            type="button"
            className="btn-resend-verify"
            onClick={handleResendVerification}
            disabled={isResending || resendCooldown > 0}
          >
            {isResending
              ? "Sending…"
              : resendCooldown > 0
              ? `Resend available in ${resendCooldown}s`
              : "Resend confirmation email"}
          </button>
        </div>
      )}

      {/* Primary CTA - Sign In button first */}
      <div className="auth-field-container">
        <button className="btn-black" type="submit" disabled={!formik.dirty || !formik.isValid}>
          <span className="signin-button-text">Sign in</span>
        </button>
      </div>

      {/* Google Sign In - below primary button */}
      {/* [2026-02-11] cookie-auth: Added mt-3 spacing between Sign in and Google buttons */}
      <div className="auth-field-container mt-3">
        <GoogleAuthButton
          onClick={() => googleLogin()}
          label={isGoogleLoading ? "Signing in..." : "Sign in with Google"}
          ariaLabel="Sign in with Google"
          disabled={isGoogleLoading}
        />
      </div>
    </form>
  );
};

export default LoginForm;
